import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  ControlCategories,
  ControlCategory,
  ControlMonitorEndpointControlSummary,
  formatLocalDate,
  getControlCategoryName,
  greaterThanToday,
  parseDate,
  queries,
  service,
  TimeFormats,
  useAuthConfig,
  ValidControl,
  ValidControlCategory,
} from "@theprelude/core";
import {
  Button,
  DateInputFormField,
  ExceptionIcon,
  Form,
  FormField,
  InputFormField,
  notifyError,
  notifySuccess,
  Overlay,
  OverlayLoading,
  TrashcanIcon,
  useConfirmSubmit,
} from "@theprelude/ds";
import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { OverlayErrorFallback } from "@/components/default-overlay";
import {
  AdditionalFilter,
  anyDomainFilter,
  anyDurationLastSeenFilter,
  anyInboxTypeFilter,
  anyLastSeenFilter,
  anyPrincipalNameFilter,
  anyRiskFilter,
  anyStatusFilter,
  anyUserTypeFilter,
  cloudAccountIdFilter,
  cloudInstanceIdFilter,
  controlCloudFilters,
  controlDeviceTypeFilters,
  controlDomainFilters,
  controlHasAgentFilters,
  controlInboxTypeFilters,
  controlLastScanFilters,
  controlLastSeenFilters,
  controlOwnershipFilters,
  controlPlatformFilters,
  controlPrincipalNamesFilters,
  controlRiskFilters,
  controlStatusFilters,
  controlUserTypeFilters,
  controlVersionFilters,
  deviceTypeFilter,
  durationLastSeenFilter,
  emailFilter,
  endpointControlStartTimeFilters,
  hostNameFilter,
  identityControlStartTimeFilters,
  lastSeenFilter,
  macAddressFilter,
  ownershipFilter,
  platformFilter,
  serialNumberFilter,
} from "@/components/filter-form/additional-filters";
import {
  convertFiltersToFormFilters,
  convertFormFiltersToFilters,
  FilterFormActions,
  FilterFormFields,
  formFilterSchema,
} from "@/components/filter-form/filter-form";
import { getOdataFilters } from "@/components/filter-form/odata";
import { useOverlay } from "@/hooks/use-overlay";
import { parseFilter } from "@/lib/odata";
import { calculateExpirationDate } from "@/queries/exceptions";
import { DeleteExceptionDialog } from "../delete-exception-modal";
import {
  useEmailControls,
  useEndpointControls,
  useEndpointControlSummaries,
  useIdentityControls,
} from "../endpoint-tables/scm-columns";
import { SaveExceptionDialog } from "../save-exception-modal";

export const addObjectExceptionSchema = z.tuple([
  z.literal("add-object-exception"),
  z.object({
    id: z.string().optional(),
    category: z.nativeEnum(ControlCategories),
  }),
]);

export type AddObjectExceptionSchema = z.infer<
  typeof addObjectExceptionSchema
>[1];

export function AddObjectExceptionOverlay({
  id,
  category,
}: AddObjectExceptionSchema) {
  const overlay = useOverlay();
  const router = useRouter();

  return (
    <Overlay
      title={
        <Suspense fallback={<TitleLoading />}>
          <Title id={id} category={category} />
        </Suspense>
      }
      position="right"
      defaultSize={60}
      hideOverlay={overlay.close}
    >
      <ErrorBoundary FallbackComponent={OverlayErrorFallback}>
        <Suspense fallback={<OverlayLoading />}>
          <div className="flex h-full flex-col gap-4 p-4 pb-0">
            <ObjectExceptionForm
              id={id}
              category={category}
              onCancel={router.history.back}
            />
          </div>
        </Suspense>
      </ErrorBoundary>
    </Overlay>
  );
}

const Title = ({ id, category }: AddObjectExceptionSchema) => {
  const { data: exception } = useObjectExceptionQuery({ id });

  return (
    <div className="flex items-center gap-2">
      <ExceptionIcon />
      {exception ? (
        <span>Edit Exception: {exception.name}</span>
      ) : (
        <span>Add Exception: {getControlCategoryName(category)}</span>
      )}
    </div>
  );
};

const TitleLoading = () => (
  <div className="flex items-center gap-2">
    <ExceptionIcon />
    <div className="h-3 w-96 animate-pulse rounded-md bg-secondary-foreground" />
  </div>
);

const formSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  category: z.nativeEnum(ControlCategories),
  filters: z
    .array(formFilterSchema)
    .min(1, "You must provide at least one filter"),
  expires: z
    .string()
    .nullable()
    .refine((date) => {
      return !date || !greaterThanToday(parseDate(date), 0);
    }, "The expiration date must be in the future"),
});

type FormSchema = z.infer<typeof formSchema>;

const useUpsertExceptionMutation = (availableFilters: AdditionalFilter[]) => {
  const authConfig = useAuthConfig();
  const overlay = useOverlay();
  const router = useRouter();

  return useMutation({
    mutationFn: async (data: FormSchema & { category: ControlCategory }) => {
      const filter = getOdataFilters(
        convertFormFiltersToFilters(data.filters, availableFilters),
      );
      const expires = data.expires
        ? calculateExpirationDate(data.expires)
        : undefined;

      if (data.id) {
        return service(authConfig).scm.updateObjectException({
          id: data.id,
          name: data.name,
          filter,
          expires,
        });
      }
      return service(authConfig).scm.createObjectException({
        name: data.name,
        category: data.category,
        filter,
        expires,
      });
    },
    onSuccess: (_result, { id, name }) => {
      notifySuccess({
        message: (
          <div className="flex items-center gap-16">
            <span>
              &quot;{name}&quot; has been {id ? "updated" : "created"}.
            </span>
            <Button
              className="whitespace-nowrap border border-stroke-input bg-transparent text-white"
              onClick={() => overlay.open("exceptions", { filter: "all" })}
            >
              View all exceptions
            </Button>
          </div>
        ),
        duration: Number.POSITIVE_INFINITY,
        dismissible: true,
      });
      router.history.back();
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to create exception.",
        error,
        dismissible: true,
      });
    },
  });
};

const useDeleteExceptionMutation = () => {
  const authConfig = useAuthConfig();
  const router = useRouter();

  return useMutation({
    mutationFn: async (id: string) =>
      service(authConfig).scm.deleteObjectException(id),
    onSuccess: () => {
      notifySuccess({
        message: "Exception has been deleted.",
        duration: Number.POSITIVE_INFINITY,
        dismissible: true,
      });
      router.history.back();
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to delete exception.",
        error,
        dismissible: true,
      });
    },
  });
};

const EMPTY_ARRAY: never[] = [];

const getEndpointFilters = (
  category: ValidControlCategory,
  controls: ValidControl[],
  endpointControls: Map<string, ControlMonitorEndpointControlSummary>,
): AdditionalFilter[] => [
  ownershipFilter,
  lastSeenFilter,
  platformFilter,
  hostNameFilter,
  serialNumberFilter,
  deviceTypeFilter,
  durationLastSeenFilter,
  cloudInstanceIdFilter,
  cloudAccountIdFilter,
  macAddressFilter,
  ...endpointControlStartTimeFilters({ category, controls, endpointControls }),
  ...controlOwnershipFilters(controls),
  ...controlLastSeenFilters(controls),
  ...controlLastScanFilters(controls),
  ...controlPlatformFilters(controls),
  ...controlDeviceTypeFilters(controls),
  ...controlVersionFilters(controls),
  ...controlHasAgentFilters(controls),
  ...controlCloudFilters(controls),
];

const getUserFilters = (controls: ValidControl[]): AdditionalFilter[] => [
  emailFilter,
  anyLastSeenFilter,
  anyStatusFilter,
  anyRiskFilter,
  anyUserTypeFilter,
  anyDurationLastSeenFilter,
  ...controlUserTypeFilters(controls),
  ...identityControlStartTimeFilters(controls),
  ...controlLastSeenFilters(controls),
  ...controlRiskFilters(controls),
  ...controlStatusFilters(controls),
];

const getInboxFilters = (controls: ValidControl[]): AdditionalFilter[] => [
  emailFilter,
  anyDomainFilter,
  anyPrincipalNameFilter,
  anyInboxTypeFilter,
  ...controlInboxTypeFilters(controls),
  ...controlDomainFilters(controls),
  ...controlPrincipalNamesFilters(controls),
];

const useAvailableFilters = (category: ControlCategory): AdditionalFilter[] => {
  const endpointControlSummaries = useEndpointControlSummaries();
  const endpointControls = useEndpointControls();
  const userControls = useIdentityControls();
  const emailControls = useEmailControls();

  return category === ControlCategories.XDR ||
    category === ControlCategories.ASSET_MANAGER ||
    category === ControlCategories.DISCOVERED_DEVICES ||
    category === ControlCategories.VULN_MANAGER
    ? getEndpointFilters(category, endpointControls, endpointControlSummaries)
    : category === ControlCategories.EMAIL
      ? getInboxFilters(emailControls)
      : category === ControlCategories.IDENTITY
        ? getUserFilters(userControls)
        : EMPTY_ARRAY;
};

type ObjectExceptionFormProps = AddObjectExceptionSchema & {
  onCancel: () => void;
};

const ObjectExceptionForm = ({
  id,
  category,
  onCancel,
}: ObjectExceptionFormProps) => {
  const saveConfirmation = useConfirmSubmit<FormSchema>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const availableFilters = useAvailableFilters(category);

  const { data: exception } = useObjectExceptionQuery({ id });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: exception?.id,
      name: exception?.name ?? "",
      category,
      filters: exception
        ? convertFiltersToFormFilters(
            parseFilter(exception.filter, availableFilters),
            availableFilters,
          )
        : [{ id: "", operator: "", value: "" }],
      expires: exception?.expires
        ? formatLocalDate(parseDate(exception.expires), TimeFormats.DATE_SHORT)
        : "",
    },
  });

  const { control, register } = form;

  const fieldArray = useFieldArray({ control, name: "filters" });

  const { mutate: upsertException, isPending: isSaving } =
    useUpsertExceptionMutation(availableFilters);

  const { mutate: deleteException, isPending: isDeleting } =
    useDeleteExceptionMutation();

  const onSubmit = () => {
    if (saveConfirmation.isOpen) {
      saveConfirmation.close();
      upsertException({ ...saveConfirmation.data, category });
    }
  };

  const onDelete = () => {
    if (exception) {
      setShowDeleteConfirmation(false);
      deleteException(exception.id);
    }
  };

  const isPending = isSaving || isDeleting;

  return (
    <Form
      {...form}
      onSubmitValid={saveConfirmation.open}
      className="flex h-full flex-col gap-4"
    >
      <InputFormField
        {...register("name")}
        label="Name"
        placeholder="Exception name"
        required
        readOnly={isPending}
        className="w-1/2"
      />
      <FilterFormFields
        fields={fieldArray.fields}
        onRemove={fieldArray.remove}
        availableFilters={availableFilters}
        readOnly={isPending}
      />
      <FormField name="filters">
        <FilterFormActions
          onAdd={fieldArray.append}
          onClear={fieldArray.remove}
          disabled={isPending}
        />
      </FormField>
      <DateInputFormField
        {...register("expires")}
        label="Exception expiration date"
        readOnly={isPending}
      />
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            type="button"
            intent="secondary"
            disabled={isPending}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            intent="primary"
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Button>
        </div>
        {exception && (
          <Button
            type="button"
            intent="negative-inverted"
            icon={<TrashcanIcon />}
            onClick={() => setShowDeleteConfirmation(true)}
          >
            Delete this exception
          </Button>
        )}
      </div>
      <SaveExceptionDialog
        open={saveConfirmation.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            saveConfirmation.close();
          }
        }}
        onConfirm={onSubmit}
      />
      <DeleteExceptionDialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        onConfirm={onDelete}
      />
    </Form>
  );
};

const useObjectExceptionQuery = ({
  id,
}: Pick<AddObjectExceptionSchema, "id">) => {
  const authConfig = useAuthConfig();
  return useSuspenseQuery({
    ...queries.scm.objectExceptions(authConfig),
    select: (exceptions) => exceptions.find((e) => e.id === id),
  });
};
